import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseOption, TranslatedColumnGroup } from '@/types/generic';
import { ColumnGroup, ColumnOption } from '@/types/table';
import { sortObjectArray } from '@/utils/array';
import { getMetricTranslations } from '@/utils/translations';

export const useGroupedOptions = (groupedOptions: ColumnGroup[]): TranslatedColumnGroup[] => {
  const { t, i18n } = useTranslation();

  return useMemo(() => {
    const translateOptions = (options: ColumnOption[]): BaseOption<string>[] =>
      sortObjectArray(
        'label',
        options.map(({ value, label, is360, isCore, isPhysical }) => {
          const { longName, description } = getMetricTranslations(t, value, label.translationNs);
          const tooltip = longName || description ? { title: longName, description } : undefined;

          return {
            value,
            label: t(label.translationKey, { ns: label.translationNs }),
            optional: {
              is360,
              isCore,
              isPhysical,
              longLabel: label.translationKeyLong
                ? t(label.translationKeyLong, { ns: label.translationNs })
                : undefined,
              ...(tooltip && { tooltip }),
            },
          };
        }),
        i18n.language,
      );

    const translatedGroups = groupedOptions.map(
      ({ header, options }): TranslatedColumnGroup => ({
        header: t(header.translationKey, { ns: header.translationNs }),
        options: sortObjectArray(
          'header',
          options.map(subGroup => ({
            header: t(subGroup.header.translationKey, { ns: subGroup.header.translationNs }),
            options: translateOptions(subGroup.options),
          })),
          i18n.language,
        ),
      }),
    );

    return sortObjectArray('header', translatedGroups, i18n.language);
  }, [groupedOptions, t, i18n.language]);
};
